import { Dropdown, DropdownOption, DropdownProps } from "core/components/dropdown/dropdown";
import useForSelectModelDropdownOptions from "core/components/dropdown/hooks/useForSelectModelDropdownOptions";
import { ForSelectModel } from "core/sharedmodels/forSelectModel";
import { MultiValue, OnChangeValue, SelectInstance, SingleValue } from "react-select";
import DropdownPermissions from "../dropdownPermissions/DropdownPermissions";
import { RefObject, useEffect, useRef, useState } from "react";
import useRestrictedDropdownOptions from "../hooks/useRestrictedDropdownOptions";

const useClearSelectedValueOnRemovedOptions = <IsMulti extends boolean = false> (
    dropdownRef: RefObject<SelectInstance<DropdownOption<string>, IsMulti>>,
    initialValuesHasLoaded: boolean,
    listenOnChangesToOptionsLength: number,
    selectedOptionOrOptions?: string | string[],
) => {
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        if (!hasInitialized && initialValuesHasLoaded) {
            setHasInitialized(true);
            return; // skip clearing logic until we are sure initialValues has been loaded.
        }

        if (!hasInitialized) return;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const triggerListenPlaceholder = listenOnChangesToOptionsLength; //We need this, so it listens to changes on dropdown-options (length) - for clearing values, when they are removed.

        const currentDropdown = dropdownRef.current;
        const anythingSelected = selectedOptionOrOptions !== undefined && selectedOptionOrOptions?.length !== 0;
        const optionHasBeenRemoved = !currentDropdown?.hasValue();

        if(currentDropdown !== null && optionHasBeenRemoved && anythingSelected) {
            currentDropdown.clearValue(); //Triggers "onChange", so we persist clearing of the option.
        }
    }, [dropdownRef, hasInitialized, initialValuesHasLoaded, listenOnChangesToOptionsLength, selectedOptionOrOptions])
}

export type ForSelectDropdownGenericProps <T> = {
    initialSelectedOption?: T | T[];
    title?: string;
    placeholder?: string;
    permissions?: DropdownPermissions;
    restrictedValues?: T[];
    disable?: boolean;
    onMenuCloseCallback?: () => void;
}

export type ForSelectDropdownProps <T, IsMulti extends boolean = false> = {
    onChangeCallback: (newValue: OnChangeValue<DropdownOption<T>, IsMulti>) => void;
    /** The fetch method that fetches ForSelectModels - use as follows: `apiFetchPromise{() => stamdataApi.getSelect()}` */
    apiFetchPromise: () => Promise<ForSelectModel[]>;
} & ForSelectDropdownGenericProps<T>
& Pick<DropdownProps<T, DropdownOption<T>, false, IsMulti>, "isMulti">;

/**
 * Generic dropdown that can be used with any endpoint returning ForSelectModel[].
 */
const ForSelectDropdown = <IsMulti extends boolean = false> ({ 
    initialSelectedOption, 
    onChangeCallback, 
    apiFetchPromise, 
    title="", 
    placeholder="", 
    permissions, 
    isMulti, 
    restrictedValues, 
    disable=false, 
    onMenuCloseCallback
} : ForSelectDropdownProps<string, IsMulti>) => {

    const ref = useRef<SelectInstance<DropdownOption<string>, IsMulti>>(null);

    const { selectedOption, setSelectedOption, dropdownOptions } = useForSelectModelDropdownOptions(apiFetchPromise, permissions, initialSelectedOption);
    const { restrictedDropdownOptions, isLoading: restrictedDropdownIsLoading } = useRestrictedDropdownOptions(dropdownOptions, restrictedValues, true);
    
    useClearSelectedValueOnRemovedOptions(
        ref, 
        !restrictedDropdownIsLoading && restrictedDropdownOptions.length > 0, 
        restrictedDropdownOptions.length, 
        selectedOption
    );

    const isDisabled = disable || (!restrictedDropdownIsLoading && restrictedDropdownOptions.length === 0);

    return (
        <div className="margin-top-s">
            {title &&
                <label>{title}</label>
            }

            <Dropdown
                ref={ref}
                placeholder={placeholder}
                options={restrictedDropdownOptions}
                onChange={newValue => {
                    const selectedOptionValue = isMulti
                        ? (newValue as MultiValue<DropdownOption<string>>).map(x => x.value)
                        : (newValue as SingleValue<DropdownOption<string>>)?.value;
                    
                    setSelectedOption(selectedOptionValue);
                    onChangeCallback(newValue);
                }}
                value={restrictedDropdownOptions.filter(x => isMulti ? (selectedOption as string[])?.includes(x.value) : (selectedOption as string) === x.value)}
                isClearable
                isDisabled={isDisabled}
                isMulti={isMulti}
                onMenuClose={onMenuCloseCallback}
            />

        </div>
    );
}

export default ForSelectDropdown;